import React from 'react';

import type { TeamMember } from '../types';

interface TeamAssignmentSectionProps {
	teamType: '' | 'vente' | 'location';
	collabLoading: boolean;
	collabError: string | null;
	displayTeam: TeamMember[];
	filteredDisplayTeam: TeamMember[];
	collabSearch: string;
	setCollabSearch: (v: string) => void;
	selectedMember: TeamMember | null;
	setSelectedMember: (m: TeamMember) => void;
	errorAssign?: string;
	onOpenAgenda: (m: TeamMember) => void;
}

function TeamAssignmentSection({
	teamType,
	collabLoading,
	collabError,
	displayTeam,
	filteredDisplayTeam,
	collabSearch,
	setCollabSearch,
	selectedMember,
	setSelectedMember,
	errorAssign,
	onOpenAgenda,
}: TeamAssignmentSectionProps) {
	if (!teamType) {
		return (
			<div className="mb-3">
				<div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
					Veuillez d'abord sélectionner le <strong>type de demande</strong> pour pouvoir affecter un collaborateur au rendez-vous.
				</div>
			</div>
		);
	}

	return (
		<div className="mb-3">
			<div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 font-semibold text-emerald-900"><span role="img" aria-label="emoji">👥</span> Affectation du Rendez-vous — Équipe {teamType === 'vente' ? 'TRANSACTION' : 'LOCATION'}</div>
			{collabLoading && (
				<div className="mb-3 grid gap-4 md:grid-cols-2">
					{Array.from({ length: 6 }).map((_, i) => (
						<div key={`s-${i}`} className="animate-pulse rounded-xl border-2 border-slate-200 bg-white p-4 text-left">
							<div className="mb-3 flex items-center gap-3">
								<div className="h-12 w-12 rounded-full bg-slate-200" />
								<div className="flex-1">
									<div className="mb-2 h-3 w-2/3 rounded bg-slate-200" />
									<div className="h-3 w-1/3 rounded bg-slate-200" />
								</div>
							</div>
							<div className="mb-3 grid grid-cols-2 gap-2">
								<div className="rounded-lg bg-slate-50 p-4" />
								<div className="rounded-lg bg-slate-50 p-4" />
							</div>
						</div>
					))}
				</div>
			)}
			{!collabLoading && collabError && (
				<div className="mb-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-700">{collabError}</div>
			)}
			{!collabLoading && !collabError && displayTeam.length === 0 && (
				<div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
					Les collaborateurs éligibles s'affichent en fonction du <strong>type de demande</strong>.
				</div>
			)}
			<div className="mb-2 flex items-center justify-between gap-2">
				<input
					value={collabSearch}
					onChange={(e) => setCollabSearch(e.target.value)}
					placeholder="Rechercher un collaborateur..."
					className="flex-1 rounded-lg border-2 px-3 py-1 text-sm"
				/>
				<span className="text-xs whitespace-nowrap text-slate-500">{filteredDisplayTeam.length} trouvé(s)</span>
			</div>
			<div className="grid max-h-80 gap-4 overflow-auto pr-1 md:grid-cols-2">
				{filteredDisplayTeam.map((m) => (
					<div
						key={m.id}
						role="button"
						tabIndex={0}
						onClick={() => setSelectedMember(m)}
						onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedMember(m); } }}
						className={[
							'text-left rounded-xl border-2 p-4 transition disabled:opacity-60 relative cursor-pointer',
							selectedMember?.id === m.id ? 'bg-emerald-50 border-emerald-500 shadow' : 'bg-white border-slate-200 hover:border-rgpd-blue',
						].join(' ')}
					>
						<div className="mb-3 flex items-center gap-3">
							<div className="flex h-12 w-12 items-center justify-center rounded-full font-bold text-white" style={{ background: m.type === 'vente' ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' : 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}>{m.initiales}</div>
							<div>
								<div className="font-bold">{m.nom}</div>
								<div className="text-sm text-slate-600">{m.role}</div>
							</div>
						</div>
						<div className="mb-3 grid grid-cols-3 gap-2">
							<div className="rounded-lg bg-slate-50 p-2 text-center">
								<div className="font-bold text-rgpd-blue">{m.rdvSemaineEnCours}</div>
								<div className="text-xs text-slate-600">RDV semaine en cours</div>
							</div>
							<div className="rounded-lg bg-slate-50 p-2 text-center">
								<div className="font-bold text-rgpd-blue">{m.rdvSemaineAVenir}</div>
								<div className="text-xs text-slate-600">RDV semaine à venir</div>
							</div>
							<div className="rounded-lg bg-slate-50 p-2 text-center">
								<div className="font-bold text-rgpd-blue">{m.tauxReussite}%</div>
								<div className="text-xs text-slate-600">Taux réussite</div>
							</div>
						</div>
						{selectedMember?.id === m.id && (
							<div className="absolute top-8 right-2 inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
								<span role="img" aria-label="check">✅</span>
								Sélectionné
							</div>
						)}
						<button
							type="button"
							onClick={(e) => { e.stopPropagation(); onOpenAgenda(m); }}
							className="absolute top-2 right-2 inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-300 bg-white/90 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
							title="Voir l'agenda"
						>
						 <span role="img" aria-label="calendar">📅</span> Agenda
						</button>
					</div>
				))}
			</div>
			{errorAssign && <div className="mt-2 text-xs text-red-600">{errorAssign}</div>}
		</div>
	);
}

export default TeamAssignmentSection;


